import { useEffect, useState } from "react";
import {
  FaLinkedinIn, FaFacebookF, FaInstagram,
  FaBars, FaTimes, FaEnvelope, FaExternalLinkAlt,
} from "react-icons/fa";
import {
  AppBar, Toolbar, Box, Typography, Button,
  IconButton, Drawer, List, ListItem, Paper,
  Chip, Stack,
} from "@mui/material";
import myResume from "./assets/myResume.pdf";
import { ColorModeToggle, useAutoColorMode } from "./colorMode";

// ─── Constants ────────────────────────────────────────────────────────────────
//
// Colors are NOT defined here. They live in src/theme.ts as light/dark tokens.
// Reference them by name in `sx` (`surface.panel`, `text.tertiary`, `divider`)
// so both color schemes stay in sync.

const NAV_H = 60;
const MAX_W = 980;
const PX    = { xs: '20px', sm: '40px', md: '60px' } as const;

/** Mode-aware hairline. Uses the CSS var so it follows the active scheme. */
const HAIRLINE = '1px solid var(--mui-palette-divider)';

// ─── Data ────────────────────────────────────────────────────────────────────

// Section 01 bio. Deliberately avoids repeating the hero line or the numbers in
// the stats strip directly above it.
const about = [
  "I have been building software since 2010, moving steadily up the stack of problems: from writing code, to designing the systems around it, to shaping how entire organizations deliver. Today that means two things. I architect distributed platforms that hold up in production, and I give engineering teams and their leaders the data to see how delivery is actually performing.",
  "The problems have ranged from surgical supply chains and clinical trial compliance, to a precision medicine platform selected by the NIH, to biodiversity mapping and workforce forecasting for contact centers. Most of it was consulting, which meant scoping the problem and convincing the client before I ever got to build anything. What connects them is the same job: learn a domain well enough to model it properly, then leave behind something the client's own team can run without me.",
];

const competencies = [
  {
    num: "01",
    title: "Enterprise Architecture",
    body: "Designing scalable distributed systems using microservices, RESTful APIs, and GraphQL. Expert in Java, Python, Node.js, and TypeScript across cloud-native and on-premises environments.",
  },
  {
    num: "02",
    title: "Big Data & Technical Leadership",
    body: "Deep expertise in Hadoop ecosystems, Spark, and cloud data platforms. Led teams of 60+ professionals, providing architectural oversight across the US, Canadian, Australian, and European markets.",
  },
  {
    num: "03",
    title: "AI & Generative AI",
    body: "Building AI-powered applications using Amazon Bedrock, Bedrock AgentCore, and Strands Agents. Hands-on with LLM orchestration, multi-agent pipelines, RAG architectures, and autonomous AI workflows for enterprise use cases.",
  },
  {
    num: "04",
    title: "Machine Learning & NLP",
    body: "Applied ML across predictive analytics, NLP, and deep learning, leveraging Scikit-learn, Spark MLlib, NLTK, and Stanford NLP for CV extraction, behavioral modeling, and clinical data analysis.",
  },
];

const experience = [
  {
    company: "NAB Vietnam",
    role: "Associate Director of Delivery Excellence",
    period: "2026 – Present",
    body: "Driving domain operating model improvements across ~170 engineering squads, modernizing processes and tooling, and building the data pipelines and dashboards that analyze and forecast delivery, performance, and workforce trends. Built a Python executive reporting engine that replaced a manual monthly cycle, and embedded DORA and FLOW metrics into delivery reporting.",
    progression: [],
  },
  {
    company: "TMA Solutions",
    role: "Senior Manager",
    period: "2013 – 2026",
    body: "Leading customer management and solution architecture, ensuring client needs are met with tailored proposals and strategic guidance across global markets.",
    progression: [
      { title: "Senior Software Engineer",   period: "Oct 2013 – Jan 2016" },
      { title: "Software Consultant",         period: "Jan 2016 – Jan 2018" },
      { title: "Senior Software Consultant",  period: "Jan 2018 – Aug 2019" },
      { title: "Subject Matter Expert",       period: "Aug 2019 – Aug 2023" },
      { title: "Senior Manager",              period: "Aug 2023 – Feb 2026" },
    ],
  },
  {
    company: "OOTech, Inc.",
    role: "Senior Engineer",
    period: "2010 – 2013",
    body: "Full-stack development and strategic team management, ensuring high-quality deliverables across diverse platforms.",
    progression: [],
  },
  {
    company: "CNC Aptech",
    role: "Lecturer",
    period: "2012 – 2013",
    body: "Java programming instruction within the fourth-semester curriculum at Aptech Education.",
    progression: [],
  },
];

const skills = [
  { category: "Languages",              items: ["Java", "Python", "JavaScript", "TypeScript", "C#"] },
  { category: "Generative AI",          items: ["Amazon Bedrock", "Bedrock AgentCore", "Strands Agents", "LLM Orchestration", "RAG", "Prompt Engineering", "Multi-Agent Systems"] },
  { category: "ML / NLP / Deep Learning", items: ["Scikit-learn", "Spark MLlib", "NLTK", "Stanford NLP", "OpenNLP", "Pandas", "NumPy", "LDA"] },
  { category: "Big Data",               items: ["Hadoop", "Apache Spark", "Hive", "Pig", "Sqoop", "HDFS", "Storm", "Cloudera"] },
  { category: "Frameworks",             items: ["Spring Boot", "Quarkus", "React", "Redux", "Flask", "FastAPI", "ASP.NET", "NestJS", "Hibernate"] },
  { category: "DevOps & Cloud",         items: ["Kubernetes", "Docker", "Jenkins", "Terraform", "GitHub Actions", "AWS", "Azure", "GCP"] },
  { category: "Methodologies",          items: ["Agile / Scrum", "Kanban", "UML", "Microservices", "SOC 2"] },
  { category: "Delivery Excellence",    items: ["DORA Metrics", "FLOW Metrics", "Delivery Analytics", "Executive Reporting", "Operating Model Design", "Poisson Forecasting"] },
];

const projects = [
  {
    name: "Preftech",
    role: "Solution Architect",
    sector: "Healthcare",
    body: "Redesigned surgical preference card systems for hospitals, building a data lake on AWS to eliminate wasted procedure prep time and supply waste.",
    stack: ["Python", "Node.js", "React", "GraphQL", "AWS Airflow", "Terraform"],
  },
  {
    name: "Point Product System (DuoSpy)",
    role: "Solution Architect",
    sector: "AI / Data Governance",
    body: "AI-driven platform that monitors changes in consumer data behavior using ML models, with automated governance alerting built on Amazon Bedrock AgentCore.",
    stack: ["Python", "FastAPI", "TypeScript", "React", "Amazon Bedrock", "Redshift"],
  },
  {
    name: "Data Architecture (DDC)",
    role: "Senior Manager · Solution Architect",
    sector: "Enterprise Data",
    body: "Centralized data architecture with Master Data Management, BI, Data Mining, Data Analytics, and ML solutions for a large corporate group.",
    stack: ["Java Spring Boot", "Hibernate", "MySQL", "Kubernetes", "Docker", "GitHub Actions"],
  },
  {
    name: "Twinalize",
    role: "Technical Leader",
    sector: "IT Infrastructure",
    body: "IT infrastructure visualization platform enabling rack layout management, device mapping, and easy-to-understand analytical reporting.",
    stack: ["Java", "Quarkus", "React", "Keycloak", "Azure", "PostgreSQL"],
  },
  {
    name: "Tricoast Education",
    role: "Solution Architect",
    sector: "EdTech",
    body: "Web application for English and French teaching with automated audio-to-text transcription for exam review, scoring, and PDF/Excel reporting.",
    stack: ["Java Spring Boot", "React", "Amazon Transcribe", "ECS", "Cognito", "S3"],
  },
  {
    name: "Amnia",
    role: "Senior Manager",
    sector: "FinTech",
    body: "Comprehensive financial system with advanced data analysis tools and an internal management platform for task tracking, chat, and document management.",
    stack: ["Java Spring Boot", "AngularJS", "Kubernetes", "Kafka", "Keycloak", "MySQL"],
  },
  {
    name: "Cinareo",
    role: "Solution Architect",
    sector: "Workforce Management",
    body: "Workforce management insights for capacity, financial, and recruitment planning integrated with Genesys WFM. Achieved SOC 2 compliance.",
    stack: ["Python", "FastAPI", "TypeScript", "React", "AWS", "Plotly"],
  },
  {
    name: "Visitor Traffic Data Management",
    role: "Solution Architect",
    sector: "Analytics",
    body: "Dashboard and data generation system for real-time visitor traffic monitoring, supporting continuous review and interaction by operations teams.",
    stack: ["Python", "FastAPI", "NestJS", "TypeScript", "React", "Amazon Bedrock"],
  },
  {
    name: "Program Management Toolkit",
    role: "Technical Architect",
    sector: "Precision Medicine",
    body: "Selected by the NIH as the platform for the Precision Medicine Initiative. Adaptive micro-frontend architecture supporting evidence-based personalized engagement.",
    stack: ["Java Spring Boot", "React", "Kubernetes", "Kafka", "AWS EKS", "New Relic"],
  },
  {
    name: "Homegrown National Park",
    role: "Solution Architect",
    sector: "Environment",
    body: "Interactive biodiversity map tracking native plant coverage across the US and Canada. Community-based conservation platform with production data migration.",
    stack: ["NestJS", "TypeScript", "React", "Mapbox", "AWS", "PostgreSQL"],
  },
  {
    name: "National Minority Quality Forum",
    role: "Technical Supporter",
    sector: "Healthcare / Analytics",
    body: "New data platform supporting 20+ years of health equity analysis for minority populations, serving data to multiple independent web applications.",
    stack: ["Java", "Spring", "Python", "Pandas", "React", "AWS EKS"],
  },
  {
    name: "Engine42",
    role: "Senior Developer",
    sector: "AI / NLP",
    body: "Predictive analytics platform using Textomics for HR/Recruiting. Built NLP pipelines to extract free-text CV data and ML models for lifecycle prediction.",
    stack: ["GATE", "OpenNLP", "Stanford NLP", "Python NLTK", "Apache Spark MLlib"],
  },
  {
    name: "PCC – PointClickCare",
    role: "Team Leader · Senior Developer",
    sector: "Healthcare Analytics",
    body: "Big data ETL analytics for skilled nursing facility (SNF) customers. Built backend scoring, Jasper reports, and Tableau dashboards on Hadoop/Spark.",
    stack: ["Java", "Spring", "Hadoop HDFS", "Spark", "PostgreSQL", "Tableau"],
  },
  {
    name: "AccuWeather Forecast",
    role: "Senior Developer",
    sector: "Data Engineering",
    body: "Cron-driven system collecting forecast data from multiple providers via Kinesis, processing and comparing against actual AccuWeather data for analytics.",
    stack: ["Python", "Flask", "Celery", "AWS Kinesis", "S3", "RDS"],
  },
  {
    name: "Sales Management System",
    role: "Senior Developer",
    sector: "Retail / F&B",
    body: "Cross-platform client-server system (Android, WPF desktop, ASP.NET web) for café and restaurant management with offline-online data synchronization.",
    stack: ["Android", "Java Web Service", "WPF", "ASP.NET MVC", "MSSQL"],
  },
  {
    name: "Ostrato",
    role: "Senior Developer",
    sector: "Cloud Management",
    body: "Cloud Service Management System enabling users to order, manage, and monitor server instances with automated scheduled report delivery via email.",
    stack: ["Java Spring MVC", "Backbone.js", "Knockout.js", "JasperReports", "PostgreSQL"],
  },
  {
    name: "Gematouch",
    role: "Senior Developer",
    sector: "Clinical Trials",
    body: "Clinical trial compliance system monitoring daily medicine intake and patient well-being, improving trial adherence through real-time tracking.",
    stack: ["Python", "Flask", "JavaScript", "Backbone.js", "Knockout.js", "PostgreSQL"],
  },
];

// Side projects, given away. Tags describe what each tool *does* rather than
// what it's built with. Visitors here care about the former.
const tools = [
  {
    name: "DV GymApp",
    href: "https://gym.duyviec.com/",
    sector: "Fitness",
    tagline: "Train with the right form",
    body: "A guided exercise companion that walks you through correct form, so every rep counts and you avoid the injuries that come from doing it wrong.",
    tags: ["Exercise Form", "Technique Guide", "Beginner Friendly"],
  },
  {
    name: "dvtono",
    href: "https://tuner.duyviec.com/",
    sector: "Music",
    tagline: "Tune anything with strings",
    body: "A browser-based instrument tuner. Open it, play a string, and it shows you how far off you are. Nothing to install, nothing to buy.",
    tags: ["Guitar", "Bass", "Violin", "Ukulele"],
  },
  {
    name: "World Cup 2026",
    href: "https://wc2026.duyviec.com/",
    sector: "Sports",
    tagline: "The whole tournament, one page",
    body: "Full match schedule, news, and analytics for the 2026 FIFA World Cup. Every group, every fixture, and the numbers behind them in a single dashboard.",
    tags: ["Schedule", "News", "Analytics"],
  },
];

const education = [
  { degree: "AWS Certified Solutions Architect – Associate", institution: "Amazon Web Services", period: "Nov 2025 – Nov 2028", type: "cert"   },
  { degree: "Gemini Certified Educator",                   institution: "Google",               period: "Dec 2025 – Dec 2028", type: "cert"   },
  { degree: "BSc in Computing",                             institution: "Greenwich University", period: "2011 – 2013",         type: "degree" },
  { degree: "Diploma in ISM & Higher Diploma in Software Engineering", institution: "Aptech Education", period: "2008 – 2010", type: "degree" },
];

const socials = [
  { Icon: FaLinkedinIn, href: "https://www.linkedin.com/in/duy-viec-29570164", label: "LinkedIn"  },
  { Icon: FaFacebookF,  href: "https://www.facebook.com/duy.viec.5",           label: "Facebook"  },
  { Icon: FaInstagram,  href: "https://www.instagram.com/viecduy",             label: "Instagram" },
];

// ─── Shared sub-component ─────────────────────────────────────────────────────

function SectionHeader({ num, title }: { num: string; title: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2.5, mb: 6.5, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 'clamp(2.5rem,5vw,4rem)', fontWeight: 700, letterSpacing: '-0.04em', color: 'surface.sectionNumber', lineHeight: 1, flexShrink: 0 }}>
        {num}
      </Typography>
      <Typography variant="h2" sx={{ fontSize: 'clamp(1.75rem,3.5vw,2.5rem)' }}>
        {title}
      </Typography>
    </Box>
  );
}

function TagChip({ label }: { label: string }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: 'surface.chip',
        border: 1,
        borderColor: 'divider',
        color: 'text.primary',
        fontSize: '0.8125rem',
        height: 'auto',
        borderRadius: '980px',
        '& .MuiChip-label': { px: '13px', py: '5px' },
      }}
    />
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useAutoColorMode();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setMenuOpen(false);

  const navLinks = [
    { label: 'About',      href: '#about'      },
    { label: 'Experience', href: '#experience' },
    { label: 'Skills',     href: '#skills'     },
    { label: 'Projects',   href: '#projects'   },
    { label: 'Free Tools', href: '#tools'      },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>

      {/* ── Nav ── */}
      <AppBar
        position="fixed"
        elevation={0}
        // `transparent` opts out of MUI's dark-mode AppBar background override,
        // which would otherwise beat the translucent token below.
        color="transparent"
        sx={{
          height: NAV_H,
          bgcolor: scrolled ? 'surface.navScrolled' : 'surface.nav',
          backdropFilter: 'saturate(180%) blur(24px)',
          WebkitBackdropFilter: 'saturate(180%) blur(24px)',
          borderBottom: 1,
          borderColor: scrolled ? 'surface.panelBorder' : 'transparent',
          transition: 'background 0.35s, border-color 0.35s',
          justifyContent: 'center',
        }}
      >
        <Toolbar
          sx={{
            maxWidth: MAX_W,
            width: '100%',
            mx: 'auto',
            px: PX,
            minHeight: `${NAV_H}px !important`,
          }}
        >
          <Typography
            component="a"
            href="#home"
            sx={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', color: '#fff', mr: 'auto' }}
          >
            DV
          </Typography>

          {/* Desktop links */}
          <Stack direction="row" spacing={4} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
            {navLinks.map(({ label, href }) => (
              <Typography
                key={href}
                component="a"
                href={href}
                sx={{
                  fontSize: '0.8125rem',
                  letterSpacing: '0.01em',
                  color: 'rgba(255,255,255,0.72)',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#fff' },
                }}
              >
                {label}
              </Typography>
            ))}
            <Button
              component="a"
              href={myResume}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              size="small"
              sx={{ bgcolor: 'primary.main', fontSize: '0.8125rem', px: 2, '&:hover': { bgcolor: 'primary.dark' } }}
            >
              Resume
            </Button>
            <ColorModeToggle onDark />
          </Stack>

          {/* Mobile: theme toggle stays reachable without opening the drawer */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
            <ColorModeToggle onDark />
            <IconButton
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
              sx={{ color: 'rgba(255,255,255,0.8)' }}
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="top"
        open={menuOpen}
        onClose={close}
        sx={{
          '& .MuiDrawer-paper': {
            top: NAV_H,
            bgcolor: 'surface.drawer',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            pt: 1.5,
            pb: 3,
            boxShadow: 'none',
          },
          '& .MuiBackdrop-root': { top: NAV_H },
        }}
      >
        <List disablePadding>
          {navLinks.map(({ label, href }) => (
            <ListItem key={href} disablePadding>
              <Typography
                component="a"
                href={href}
                onClick={close}
                sx={{
                  display: 'block',
                  width: '100%',
                  px: PX,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.8)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  transition: 'color 0.2s, background 0.2s',
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                {label}
              </Typography>
            </ListItem>
          ))}
          <ListItem sx={{ px: PX, pt: 2.5 }}>
            <Button
              component="a"
              href={myResume}
              target="_blank"
              rel="noreferrer"
              fullWidth
              variant="contained"
              onClick={close}
              sx={{ bgcolor: 'primary.main', borderRadius: '980px', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              Resume
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* ── Hero ── */}
      <Box
        component="section"
        id="home"
        sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100dvh', bgcolor: 'surface.panel', pt: `${NAV_H}px` }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: PX,
            py: { xs: '48px', md: '100px' },
            maxWidth: MAX_W,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Typography
            variant="h1"
            sx={{ fontSize: 'clamp(3rem,8.5vw,7rem)', color: '#fff', mb: 3.5 }}
          >
            Duy Viec
          </Typography>
          <Typography
            sx={{ fontSize: 'clamp(1rem,2vw,1.25rem)', color: 'rgba(255,255,255,0.55)', maxWidth: 560, lineHeight: 1.65, mb: 2.25 }}
          >
            Solution Architect and engineering leader with 16+ years in software. I design
            scalable distributed systems, cloud-native platforms, and big-data applications,
            build Generative AI agents and LLM pipelines on Amazon Bedrock, and lead delivery
            for teams across the US, Canada, Australia, and Europe.
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', mb: 5 }}>
            <Box
              component="a"
              href="mailto:minhduyvl@gmail.com"
              sx={{ color: 'rgba(255,255,255,0.5)', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
            >
              minhduyvl@gmail.com
            </Box>
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              component="a"
              href="#projects"
              variant="contained"
              sx={{ bgcolor: 'primary.main', px: 3.25, py: 1.625, fontSize: '0.9375rem', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              View Projects
            </Button>
            <Button
              component="a"
              href={myResume}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              sx={{
                px: 3.25,
                py: 1.625,
                fontSize: '0.9375rem',
                color: 'rgba(255,255,255,0.78)',
                borderColor: 'rgba(255,255,255,0.28)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff', borderColor: 'rgba(255,255,255,0.45)' },
              }}
            >
              My Resume
            </Button>
          </Stack>
        </Box>

        {/* Stats strip */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {[
            { num: "16+", label: "Years of Experience" },
            { num: "60+", label: "Team Members Led" },
            { num: "17+", label: "Major Projects"   },
            { num: "3",   label: "Free Tools"      },
          ].map(({ num, label }, i) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                py: { xs: '20px', md: '36px' },
                px: PX,
                borderRight: {
                  xs: i % 2 === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                  md: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                },
                borderTop: { xs: i >= 2 ? '1px solid rgba(255,255,255,0.08)' : 'none', md: 'none' },
              }}
            >
              <Typography sx={{ fontSize: 'clamp(1.75rem,4vw,2.5rem)', fontWeight: 700, letterSpacing: '-0.03em', color: '#fff', lineHeight: 1 }}>
                {num}
              </Typography>
              <Typography sx={{ fontSize: 'clamp(0.6875rem,1.2vw,0.8125rem)', fontWeight: 500, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── About / Competencies ── */}
      <Box component="section" id="about" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'background.default' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="01" title="About" />
          <Stack spacing={2.5} sx={{ maxWidth: 720, mb: 7 }}>
            {about.map((para, i) => (
              <Typography key={i} sx={{ fontSize: 'clamp(1rem,1.8vw,1.125rem)', color: 'text.secondary', lineHeight: 1.72 }}>
                {para}
              </Typography>
            ))}
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' },
              gap: '1px',
              bgcolor: 'divider',
              border: HAIRLINE,
              borderRadius: '20px',
              overflow: 'hidden',
            }}
          >
            {competencies.map(({ num, title, body }) => (
              <Box key={num} sx={{ bgcolor: 'background.default', p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'primary.main' }}>
                  {num}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.015em' }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary', lineHeight: 1.65 }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Experience ── */}
      <Box component="section" id="experience" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'surface.band' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="02" title="Experience" />
          {experience.map(({ company, role, period, body, progression }, idx) => (
            <Box
              key={company}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                gap: { xs: '6px', sm: '32px' },
                py: 5,
                borderBottom: idx < experience.length - 1 ? HAIRLINE : 'none',
                '&:first-of-type': { pt: 0 },
                '&:last-of-type': { pb: 0 },
              }}
            >
              <Box sx={{ pt: { xs: 0, sm: 0.5 } }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.tertiary' }}>
                  {period}
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
                  <Typography variant="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {company}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'primary.main', flexShrink: 0 }}>
                    {role}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary', lineHeight: 1.65, mb: 2.5 }}>
                  {body}
                </Typography>
                {progression.length > 0 && (
                  <Box sx={{ border: HAIRLINE, borderRadius: '14px', overflow: 'hidden' }}>
                    {progression.map(({ title, period: p }, i) => (
                      <Box
                        key={title}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.25,
                          borderBottom: i < progression.length - 1 ? HAIRLINE : 'none',
                          gap: 1.5,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{title}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.tertiary', whiteSpace: 'nowrap' }}>{p}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Skills ── */}
      <Box component="section" id="skills" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'background.default' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="03" title="Skills" />
          {skills.map(({ category, items }, idx) => (
            <Box
              key={category}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                gap: { xs: 1.5, sm: 3 },
                alignItems: 'start',
                py: 3,
                borderBottom: idx < skills.length - 1 ? HAIRLINE : 'none',
                '&:first-of-type': { pt: 0 },
                '&:last-of-type': { pb: 0 },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.tertiary', pt: { xs: 0, sm: '6px' } }}>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {items.map(item => <TagChip key={item} label={item} />)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Projects ── */}
      <Box component="section" id="projects" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'surface.band' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="04" title="Projects" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' }, gap: 2 }}>
            {projects.map(({ name, role, sector, body, stack }) => (
              <Paper
                key={name}
                elevation={0}
                sx={{
                  border: HAIRLINE,
                  borderRadius: '16px',
                  p: { xs: 2.5, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  height: '100%',
                  transition: 'box-shadow 0.22s, border-color 0.22s',
                  '&:hover': { borderColor: 'surface.accentBorder', boxShadow: '0 4px 24px var(--mui-palette-surface-cardShadowColor)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'primary.main', mb: '5px' }}>
                      {sector}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.3 }}>
                      {name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0, maxWidth: 130 }}>
                    {role.split(' · ').map((r, i) => (
                      <Typography key={i} sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.tertiary', textAlign: 'right', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                        {r}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.65, flex: 1 }}>
                  {body}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: 'auto' }}>
                  {stack.map(t => <TagChip key={t} label={t} />)}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Free Tools ── */}
      <Box component="section" id="tools" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'background.default' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="05" title="Free Tools" />
          <Typography sx={{ fontSize: 'clamp(1rem,1.8vw,1.125rem)', color: 'text.secondary', lineHeight: 1.72, maxWidth: 720, mb: 6 }}>
            Outside of client work I build small tools and give them away. No accounts,
            no ads, no paywall. Just something useful that runs in your browser. Each of
            these started as a problem I had, and stayed online because other people found
            it useful too.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit,minmax(280px,1fr))' }, gap: 2 }}>
            {tools.map(({ name, href, sector, tagline, body, tags }) => (
              <Paper
                key={name}
                component="a"
                href={href}
                target="_blank"
                rel="noreferrer"
                elevation={0}
                sx={{
                  border: HAIRLINE,
                  borderRadius: '16px',
                  p: { xs: 2.5, md: 3.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  height: '100%',
                  transition: 'box-shadow 0.22s, border-color 0.22s, transform 0.22s',
                  '&:hover': {
                    borderColor: 'surface.accentBorder',
                    boxShadow: '0 4px 24px var(--mui-palette-surface-cardShadowColor)',
                    transform: 'translateY(-2px)',
                  },
                  '&:hover .tool-arrow': { color: 'primary.main', transform: 'translate(2px,-2px)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'primary.main', mb: '5px' }}>
                      {sector}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.3 }}>
                      {name}
                    </Typography>
                  </Box>
                  <Box
                    className="tool-arrow"
                    aria-hidden="true"
                    sx={{ color: 'text.tertiary', fontSize: '0.8125rem', mt: '2px', flexShrink: 0, transition: 'color 0.22s, transform 0.22s' }}
                  >
                    <FaExternalLinkAlt />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.01em', color: 'text.primary' }}>
                  {tagline}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.65, flex: 1 }}>
                  {body}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {tags.map(t => <TagChip key={t} label={t} />)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 'auto', pt: 1.5, borderTop: HAIRLINE }}>
                  <Chip
                    label="Free · No sign-up"
                    size="small"
                    sx={{
                      bgcolor: 'surface.accentSoft',
                      color: 'primary.main',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      height: 'auto',
                      borderRadius: '980px',
                      '& .MuiChip-label': { px: '10px', py: '3px' },
                    }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.tertiary', whiteSpace: 'nowrap' }}>
                    {href.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Education ── */}
      <Box component="section" id="education" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'surface.band' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <SectionHeader num="06" title="Education & Certifications" />
          {education.map(({ degree, institution, period, type }, idx) => (
            <Box
              key={degree}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 3,
                py: 3.5,
                borderBottom: idx < education.length - 1 ? HAIRLINE : 'none',
                flexWrap: 'wrap',
                '&:first-of-type': { pt: 0 },
                '&:last-of-type': { pb: 0 },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                  {degree}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {institution}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.tertiary', whiteSpace: 'nowrap' }}>
                  {period}
                </Typography>
                <Chip
                  label={type === 'cert' ? 'Certification' : 'Degree'}
                  size="small"
                  sx={{
                    bgcolor: type === 'cert' ? 'surface.accentSoft' : 'background.default',
                    color: type === 'cert' ? 'primary.main' : 'text.tertiary',
                    border: type === 'cert' ? 'none' : HAIRLINE,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    height: 'auto',
                    borderRadius: '980px',
                    '& .MuiChip-label': { px: '10px', py: '3px' },
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Contact ── */}
      <Box component="section" id="contact" sx={{ py: 'clamp(72px,10vw,120px)', bgcolor: 'surface.panel', textAlign: 'center' }}>
        <Box sx={{ maxWidth: MAX_W, mx: 'auto', px: PX }}>
          <Typography variant="h2" sx={{ fontSize: 'clamp(2rem,5vw,3.5rem)', color: '#fff', lineHeight: 1.06, mb: 2 }}>
            Let's Build Something
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: 'rgba(255,255,255,0.48)', mb: 5, lineHeight: 1.6 }}>
            Open to senior leadership, solution architecture, and advisory roles.
            Based in Ho Chi Minh City, Vietnam.
          </Typography>
          <Box
            component="a"
            href="mailto:minhduyvl@gmail.com"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              fontSize: '1.0625rem',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.85)',
              mb: 5,
              px: 3.5,
              py: 1.75,
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: '980px',
              transition: 'all 0.22s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff', borderColor: 'rgba(255,255,255,0.32)' },
            }}
          >
            <FaEnvelope aria-hidden="true" />
            minhduyvl@gmail.com
          </Box>
          <Stack direction="row" spacing={1.75} justifyContent="center">
            {socials.map(({ Icon, href, label }) => (
              <Box
                key={label}
                component="a"
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: '1.1rem',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'background 0.22s, color 0.22s, transform 0.22s, border-color 0.22s',
                  '&:hover': { bgcolor: 'primary.main', color: '#fff', borderColor: 'transparent', transform: 'translateY(-2px)' },
                }}
              >
                <Icon />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box component="footer" sx={{ py: 3, bgcolor: 'surface.panel', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <Box
          sx={{
            maxWidth: MAX_W,
            mx: 'auto',
            px: PX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.35)' }}>
            © {new Date().getFullYear()} Duy Viec
          </Typography>
          <Typography
            component="a"
            href="https://duyviec.com"
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.35)', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.65)' } }}
          >
            duyviec.com
          </Typography>
        </Box>
      </Box>

    </Box>
  );
}
